import Box from "@amzn/awsui-components-react/polaris/box";
import Badge from "@amzn/awsui-components-react/polaris/badge";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Table from "@amzn/awsui-components-react/polaris/table";
import ExternalLink from '../Common/ExternalLink';
import { ISSUE_URLS } from '../Common/ZestConstants';

const ValueWithLabel = ({ label, children }) => (
    <div>
        <Box variant="awsui-key-label">{label}</Box>
        <div>{children}</div>
    </div>
);

/**
 * Determines if an issue ID is a Shepherd issue (UUID format) vs Jira.
 * Matches the backend logic in zest.py parse_issue_ids().
 */
function isShepherdId(issueId) {
    return issueId && issueId.length === 36 && issueId.split('-').length === 5;
}

function getIssueUrl(issueId) {
    const isBeta = window.location.hostname.includes('beta');
    if (isShepherdId(issueId)) {
        return isBeta ? ISSUE_URLS.SHEPHERD_BETA : ISSUE_URLS.SHEPHERD_PROD;
    }
    return isBeta ? ISSUE_URLS.JIRA_STAGING : ISSUE_URLS.JIRA_PROD;
}

export default function ClosureResultsPanel(props) {
    let results = props.results;

    if (typeof results === 'string') {
        try {
            results = JSON.parse(results);
        } catch (e) {
            return <Box>Failed to parse closure results.</Box>;
        }
    }

    const summary = results.summary || {};
    const closedTickets = results.closed_tickets || [];
    const failedTickets = results.failed_to_close_tickets || [];

    return (
        <SpaceBetween size="l">
            <Container header={<Header variant="h2">Closure Summary</Header>}>
                <ColumnLayout columns={4} variant="text-grid">
                    <ValueWithLabel label="Run ID">{results.run_id || '--'}</ValueWithLabel>
                    <ValueWithLabel label="Requested By">{results.requester_alias || '--'}</ValueWithLabel>
                    <ValueWithLabel label="Total Issues">{summary.total ?? '--'}</ValueWithLabel>
                    <ValueWithLabel label="Result">
                        <SpaceBetween direction="horizontal" size="xs">
                            {summary.closed > 0 && <Badge color="green">{summary.closed} Closed</Badge>}
                            {summary.failed > 0 && <Badge color="red">{summary.failed} Failed</Badge>}
                            {summary.closed === 0 && summary.failed === 0 && '--'}
                        </SpaceBetween>
                    </ValueWithLabel>
                </ColumnLayout>
            </Container>

            {closedTickets.length > 0 && (
                <Table
                    header={<Header variant="h2">Closed Issues - {closedTickets.length}</Header>}
                    columnDefinitions={[
                        {
                            id: "id",
                            header: "Issue ID",
                            cell: item => (
                                <ExternalLink
                                    base={getIssueUrl(item)}
                                    value={item}
                                />
                            )
                        },
                        {
                            id: "type",
                            header: "Type",
                            cell: item => isShepherdId(item)
                                ? <Badge color="green">Shepherd</Badge>
                                : <Badge color="blue">Jira</Badge>
                        }
                    ]}
                    items={closedTickets}
                    sortingDisabled
                    empty={<Box textAlign="center"><b>No closed issues</b></Box>}
                />
            )}

            {failedTickets.length > 0 && (
                <Table
                    header={<Header variant="h2">Failed Issues - {failedTickets.length}</Header>}
                    columnDefinitions={[
                        {
                            id: "id",
                            header: "Issue ID",
                            cell: item => (
                                <ExternalLink
                                    base={getIssueUrl(item.id)}
                                    value={item.id}
                                />
                            )
                        },
                        {
                            id: "type",
                            header: "Type",
                            cell: item => isShepherdId(item.id)
                                ? <Badge color="green">Shepherd</Badge>
                                : <Badge color="blue">Jira</Badge>
                        },
                        {
                            id: "error",
                            header: "Error",
                            cell: item => item.error || '--'
                        }
                    ]}
                    items={failedTickets}
                    sortingDisabled
                    empty={<Box textAlign="center"><b>No failed issues</b></Box>}
                />
            )}
        </SpaceBetween>
    );
}
